import React, { createContext, useContext, useState, useEffect } from 'react'

export type UIThemeMode = 'default' | 'paper-loan-ledger'
export type DeviceViewport = 'auto' | 'pc' | 'tablet' | 'phone'

interface UIThemeContextType {
  theme: UIThemeMode
  setTheme: (theme: UIThemeMode) => void
  deviceView: DeviceViewport
  setDeviceView: (view: DeviceViewport) => void
  isPaperLoanUI: boolean
}

const UIThemeContext = createContext<UIThemeContextType>({
  theme: 'default',
  setTheme: () => {},
  deviceView: 'auto',
  setDeviceView: () => {},
  isPaperLoanUI: false,
})

export const UIThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<UIThemeMode>(() => {
    try {
      const saved = localStorage.getItem('cooperp_ui_theme') as UIThemeMode
      return saved === 'paper-loan-ledger' ? 'paper-loan-ledger' : 'default'
    } catch {
      return 'default'
    }
  })

  const [deviceView, setDeviceViewState] = useState<DeviceViewport>(() => {
    try {
      const saved = localStorage.getItem('cooperp_device_view') as DeviceViewport
      return saved && ['auto', 'pc', 'tablet', 'phone'].includes(saved) ? saved : 'auto'
    } catch {
      return 'auto'
    }
  })

  const setTheme = (newTheme: UIThemeMode) => {
    setThemeState(newTheme)
    try {
      localStorage.setItem('cooperp_ui_theme', newTheme)
    } catch (e) {
      console.error('Failed to save UI theme to localStorage:', e)
    }
  }

  const setDeviceView = (newView: DeviceViewport) => {
    setDeviceViewState(newView)
    try {
      localStorage.setItem('cooperp_device_view', newView)
    } catch (e) {
      console.error('Failed to save device view to localStorage:', e)
    }
  }

  useEffect(() => {
    if (theme === 'paper-loan-ledger') {
      document.documentElement.classList.add('theme-paper-loan')
    } else {
      document.documentElement.classList.remove('theme-paper-loan')
    }
  }, [theme])

  return (
    <UIThemeContext.Provider
      value={{
        theme,
        setTheme,
        deviceView,
        setDeviceView,
        isPaperLoanUI: theme === 'paper-loan-ledger',
      }}
    >
      {children}
    </UIThemeContext.Provider>
  )
}

export function useUITheme() {
  return useContext(UIThemeContext)
}
